import { FC, useMemo } from 'react';
import { useSites, useSKU } from 'hooks';
import {
  calculateItemsPerPackage,
  calculateTotalItems,
} from 'utils/itemsHelper';
import { Box, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { Order } from 'types/order';
import FormikTextArea from 'components/primitives/FormikTextArea';
import { Form, Formik } from 'formik';
import { Link } from 'react-router-dom';

type Props = {
  order: Order;
  sites?: Array<any>;
  values?: {
    site: string;
    notes?: string;
  };
};

export const GeneralInfo: FC<Props> = ({ order = {} }) => {
  const { data: sites = [] } = useSites();
  const { data } = useSKU(
    'package',
    order?.washHub?._id,
    undefined,
    undefined,
    true,
  );
  const details = order?.details || [];
  const allSkuDetails = order?.skuDetails || [];
  const skuNameMapping: { [key: string]: string } = {};
  allSkuDetails.map((s) => (skuNameMapping[s._id] = s.name));

  const selectedSite = sites?.find((s) => s._id === order?.site?._id);
  const deliveryInstructions = selectedSite?.deliveryInstructions || '-';
  const orderNotes = order.notes || '-';
  const initialValues = {
    notes: order.notes || '',
    deliveryInstructions: selectedSite?.deliveryInstructions,
  };

  const { totalBoxQuantity, totalItemsQuantity } = useMemo(() => {
    return details.reduce(
      (totals, d) => {
        const sku = data?.find((s) => s._id === d.sku);
        const itemsPerPackage = calculateItemsPerPackage(sku);
        const totalItemsForCurrentDetail = calculateTotalItems(
          d.casesCount,
          itemsPerPackage,
        );

        return {
          totalBoxQuantity:
            totals.totalBoxQuantity + (Number(d.casesCount) || 0),
          totalItemsQuantity:
            totals.totalItemsQuantity + totalItemsForCurrentDetail,
        };
      },
      { totalBoxQuantity: 0, totalItemsQuantity: 0 },
    );
  }, [data, details]);

  return (
    <Flex mt="20px" gap="20px" direction="column">
      <SimpleGrid
        maxH="300px"
        overflow="auto"
        border="1px solid #2FB64A"
        borderRadius="5px"
        alignSelf="center"
        w="100%"
        templateColumns={['1fr', '1fr']}
        columns={[1, 1, 2]}>
        <Flex
          flex={1}
          direction="column"
          borderBottom={{
            base: '1px solid #2FB64A',
            md: 'none',
          }}
          fontSize="14px">
          <Flex
            borderBottom={'1px solid #2FB64A'}
            px="30px"
            py="10px"
            align="center"
            gap="20px">
            <Text flex={2} fontWeight="bold">
              Package SKU
            </Text>
            <Text textAlign="left" fontWeight="bold" flex={1}>
              Case Quantity
            </Text>
            <Text textAlign="left" fontWeight="bold" flex={1}>
              Items per Package
            </Text>
            <Text textAlign="left" fontWeight="bold" flex={1}>
              Total Items
            </Text>
          </Flex>
          <Flex direction="column">
            {details.map((d, i) => {
              const sku = data?.find((s) => s._id === d.sku);
              const itemsPerPackage = calculateItemsPerPackage(sku);
              const totalItems = calculateTotalItems(
                d.casesCount,
                itemsPerPackage,
              );
              return (
                <Flex
                  borderBottom={
                    order?.details?.length === i + 1 ? '' : '1px solid #2FB64A'
                  }
                  px="30px"
                  py="10px"
                  key={d._id}
                  align="center"
                  gap="20px">
                  <Text flex={2}>
                    {d.sku && skuNameMapping?.[d.sku]
                      ? skuNameMapping?.[d.sku]
                      : ''}
                  </Text>
                  <Text textAlign="left" flex={1}>
                    {d.casesCount || '0'}
                  </Text>
                  <Text textAlign="left" flex={1}>
                    {itemsPerPackage || '0'}
                  </Text>
                  <Text textAlign="left" flex={1}>
                    {totalItems}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
          {/* Totals Row */}
          <Flex
            px="30px"
            py="10px"
            gap="20px"
            align="center"
            borderTop="1px solid #2FB64A"
            bg="green.100"
            alignSelf="center"
            w="100%">
            <Text fontWeight="bold" flex={2}>
              Total
            </Text>
            <Text fontWeight="bold" flex={1}>
              {totalBoxQuantity}
            </Text>
            <Text flex={1}>-</Text>
            <Text fontWeight="bold" flex={1}>
              {totalItemsQuantity}
            </Text>
          </Flex>
        </Flex>
      </SimpleGrid>

      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={() => {}}>
        <Form>
          <Flex justify="space-between" mt="20px" gap="20px">
            <Box flex={1}>
              <FormikTextArea
                name="notes"
                label="Customer Order Notes"
                description="Can also include notes to drivers (such as equipment) as this is visible in-app"
                value={orderNotes}
                isReadOnly
              />
            </Box>
            <Box flex={1}>
              <FormikTextArea
                name="deliveryInstructions"
                label="Site Delivery Instructions"
                description={
                  <>
                    Visible every delivery in-app.{' '}
                    <Link
                      to={`/customer-sites/${selectedSite?._id}`}
                      style={{ color: '#2FB64A', fontWeight: 600 }}>
                      Edit in customer details
                    </Link>
                  </>
                }
                value={deliveryInstructions}
                isReadOnly
              />
            </Box>
          </Flex>
        </Form>
      </Formik>
    </Flex>
  );
};
